import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronRight,
  CircleAlert,
  ClipboardCheck,
  Eye,
  EyeOff,
  FileText,
  FolderOpen,
  Globe2,
  History,
  LockKeyhole,
  LogIn,
  LogOut,
  Map,
  Menu,
  MessageCircle,
  Plane,
  Route,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db, googleProvider } from "./firebase.js";

const ADMIN_EMAIL = "metalcovich@gmail.com";
const WHATSAPP = "351965054070";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  country: "Portugal",
  nationality: "Brasileira",
  objective: "planejamento",
  migrationStatus: "regular",
  residenceYears: "",
  age: "",
  brazilContributionYears: "",
  foreignContributionYears: "",
  currentIncomeRange: "",
  futureIncomeGoal: "",
  consent: false,
};

const steps = [
  { title: "Seu momento", subtitle: "Onde você está e o que deseja organizar" },
  { title: "Trajetória internacional", subtitle: "Contexto migratório para a triagem" },
  { title: "Histórico previdenciário", subtitle: "Períodos de contribuição informados por você" },
  { title: "Contato e consentimento", subtitle: "Receba e salve seu mapa inicial" },
];

function App() {
  const [route, setRoute] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [users, setUsers] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [simulation, setSimulation] = useState(null);

  const isBootstrapAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL;
  const role = normalizeRole(profile?.role || (isBootstrapAdmin ? "admin" : "client"));
  const accountActive = isBootstrapAdmin || profile?.status !== "blocked";
  const realIsAdmin = accountActive && (isBootstrapAdmin || role === "admin");
  const realIsLawyer = accountActive && role === "lawyer";
  const viewRole = simulation?.role || role;
  const isAdmin = !simulation && realIsAdmin;
  const isLawyer = accountActive && (simulation?.role === "lawyer" || (!simulation && realIsLawyer));
  const isProfessional = isAdmin || isLawyer;
  const canManageAccess = realIsAdmin;
  const canStartAssessment = !user || (accountActive && !isProfessional);
  const simulatedClientProfile = simulation?.role === "client"
    ? { name: simulation.name || "Cliente de teste", role: "client", status: "active" }
    : profile;

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    if (!nextUser) {
      setProfile(null);
      setProfileLoading(false);
    } else {
      setProfileLoading(true);
    }
    setAuthLoading(false);
  }), []);

  useEffect(() => {
    if (!user) return undefined;
    const bootstrap = user.email?.toLowerCase() === ADMIN_EMAIL;
    const fallback = {
      name: user.displayName || (bootstrap ? "Administrador" : "Cliente"),
      email: user.email?.toLowerCase() || "",
      role: bootstrap ? "admin" : "client",
      status: "active",
    };
    const profileRef = doc(db, "users", user.uid);
    let creatingProfile = false;
    return onSnapshot(profileRef, async (snap) => {
      if (snap.exists()) {
        setProfile(snap.data());
        setProfileLoading(false);
        return;
      }
      if (creatingProfile) return;
      creatingProfile = true;
      try {
        await setDoc(profileRef, { ...fallback, createdAt: serverTimestamp(), lastLoginAt: serverTimestamp() });
      } catch {
        setProfile(fallback);
        setProfileLoading(false);
      }
    }, () => {
      setProfile(fallback);
      setProfileLoading(false);
    });
  }, [user]);

  useEffect(() => {
    if (!user || profileLoading || !profile?.role) return;
    setDoc(doc(db, "users", user.uid), { lastLoginAt: serverTimestamp() }, { merge: true }).catch(() => undefined);
  }, [user, profileLoading, profile?.role]);

  useEffect(() => {
    if (!user || (!profileLoading && !realIsAdmin)) setSimulation(null);
  }, [user, profileLoading, realIsAdmin]);

  useEffect(() => {
    if (user && isProfessional && route === "triage") setRoute("cases");
  }, [user, isProfessional, route]);

  useEffect(() => {
    if (!user || route !== "account") return undefined;
    const ownerId = simulation?.role === "client" ? simulation.userId : user.uid;
    if (!ownerId) {
      setAssessments([]);
      return undefined;
    }
    const assessmentsQuery = query(collection(db, "assessments"), where("ownerId", "==", ownerId), limit(30));
    return onSnapshot(assessmentsQuery, (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAssessments(rows);
    });
  }, [user, route, simulation]);

  useEffect(() => {
    if (!(realIsAdmin || realIsLawyer) || route !== "cases") return undefined;
    const leadsQuery = query(collection(db, "leads"), orderBy("createdAt", "desc"), limit(80));
    return onSnapshot(leadsQuery, (snap) => setLeads(snap.docs.map((item) => ({ id: item.id, ...item.data() }))));
  }, [realIsAdmin, realIsLawyer, route]);

  useEffect(() => {
    if (!canManageAccess || route !== "admin") return undefined;
    const usersQuery = query(collection(db, "users"), limit(100));
    return onSnapshot(usersQuery, (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""), "pt"));
      setUsers(rows);
    });
  }, [canManageAccess, route]);

  useEffect(() => {
    if (!canManageAccess || route !== "admin") return undefined;
    const logsQuery = query(collection(db, "accessLogs"), orderBy("createdAt", "desc"), limit(30));
    return onSnapshot(logsQuery, (snap) => {
      setAccessLogs(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    }, () => setAccessLogs([]));
  }, [canManageAccess, route]);

  const navigate = (next) => {
    setRoute(next);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startSimulation = (nextSimulation) => {
    if (!realIsAdmin) return;
    setSimulation(nextSimulation);
    setRoute(nextSimulation.role === "lawyer" ? "cases" : "account");
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const stopSimulation = () => {
    setSimulation(null);
    setRoute("admin");
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startAssessment = () => {
    setStep(0);
    setResult(null);
    navigate("triage");
  };

  const submitAssessment = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.consent) {
      setNotice({ type: "error", text: "Informe nome, WhatsApp e aceite o consentimento para continuar." });
      return;
    }
    if (simulation) {
      setResult(buildResult(form));
      setRoute("result");
      setNotice({ type: "success", text: "Triagem simulada. Nenhum dado foi salvo no servidor." });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setBusy(true);
    setNotice(null);
    const generated = buildResult(form);
    try {
      const payload = {
        ...form,
        priority: generated.priority,
        source: "web-mvp",
        status: "new",
        consent: true,
        createdAt: serverTimestamp(),
      };
      if (user) {
        const batch = writeBatch(db);
        batch.set(doc(collection(db, "leads")), payload);
        batch.set(doc(collection(db, "assessments")), {
          ...payload,
          ownerId: user.uid,
          result: generated,
        });
        await batch.commit();
      } else {
        await addDoc(collection(db, "leads"), payload);
      }
      setResult(generated);
      setRoute("result");
    } catch (error) {
      setNotice({ type: "error", text: readableError(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      {(authLoading || (user && profileLoading)) && <BootScreen />}
      <Header
        route={route}
        user={user}
        role={viewRole}
        accountActive={accountActive}
        isAdmin={isAdmin}
        isProfessional={isProfessional}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        navigate={navigate}
        openAuth={() => setAuthOpen(true)}
      />
      {simulation && <SimulationBar simulation={simulation} stopSimulation={stopSimulation} />}
      {notice && <Toast notice={notice} close={() => setNotice(null)} />}
      <main>
        {route === "home" && <Home startAssessment={startAssessment} canStartAssessment={canStartAssessment} openCases={() => navigate("cases")} />}
        {route === "triage" && (
          <Assessment
            form={form}
            setForm={setForm}
            step={step}
            setStep={setStep}
            submit={submitAssessment}
            busy={busy}
          />
        )}
        {route === "result" && <Result result={result || buildResult(form)} form={form} restart={startAssessment} />}
        {route === "account" && <ClientArea user={user} profile={simulatedClientProfile} accountActive={accountActive} assessments={assessments} openAuth={() => setAuthOpen(true)} startAssessment={startAssessment} />}
        {route === "cases" && <CasesPanel user={user} isProfessional={isProfessional} isAdmin={isAdmin} leads={leads} openAuth={() => setAuthOpen(true)} setNotice={setNotice} />}
        {route === "admin" && <AdminPanel user={user} isAdmin={isAdmin} users={users} accessLogs={accessLogs} openAuth={() => setAuthOpen(true)} setNotice={setNotice} startSimulation={startSimulation} />}
      </main>
      <Footer startAssessment={startAssessment} canStartAssessment={canStartAssessment} openCases={() => navigate("cases")} />
      {authOpen && (
        <AuthModal
          mode={authMode}
          setMode={setAuthMode}
          close={() => setAuthOpen(false)}
          setNotice={setNotice}
          setBusy={setBusy}
          busy={busy}
        />
      )}
    </div>
  );
}

function SimulationBar({ simulation, stopSimulation }) {
  return <aside className="simulation-bar" role="status">
    <div><Eye size={19} /><span><strong>Modo de simulação: {roleLabel(simulation.role)}</strong><small>Visualizando como {simulation.name || roleLabel(simulation.role)}. Sua conta continua sendo administradora.</small></span></div>
    <button onClick={stopSimulation}><ArrowLeft size={17} /> Voltar à administração</button>
  </aside>;
}

function Header({ route, user, role, accountActive, isAdmin, isProfessional, menuOpen, setMenuOpen, navigate, openAuth }) {
  return (
    <header className="site-header">
      <button className="brand" onClick={() => navigate("home")} aria-label="Ir para início">
        <span className="brand-mark"><Globe2 size={24} /></span>
        <span><strong>JusPrevi</strong><small>Imigração e Previdência Internacional</small></span>
      </button>
      <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Abrir menu">
        {menuOpen ? <X /> : <Menu />}
      </button>
      <nav className={menuOpen ? "open" : ""}>
        <button className={route === "home" ? "active" : ""} onClick={() => navigate("home")}>Início</button>
        {(!user || (role === "client" && accountActive)) && <button className={route === "triage" ? "active" : ""} onClick={() => navigate("triage")}>Fazer triagem</button>}
        {user && (role === "client" || !accountActive) && <button className={route === "account" ? "active" : ""} onClick={() => navigate("account")}>{accountActive ? "Minha área" : "Conta bloqueada"}</button>}
        {isProfessional && <button className={route === "cases" ? "active" : ""} onClick={() => navigate("cases")}>Casos</button>}
        {isAdmin && <button className={route === "admin" ? "active" : ""} onClick={() => navigate("admin")}>Administração</button>}
        {user ? (
          <button className="nav-account" onClick={() => signOut(auth)}><LogOut size={17} /> Sair</button>
        ) : (
          <button className="nav-account" onClick={openAuth}><LogIn size={17} /> Entrar</button>
        )}
      </nav>
    </header>
  );
}

function Home({ startAssessment, canStartAssessment, openCases }) {
  return (
    <>
      <section className="hero">
        <div className="hero-content">
          <p className="eyebrow">Brasileiros no exterior</p>
          <h1>Organize sua vida entre países com mais clareza.</h1>
          <p className="hero-copy">Uma triagem inicial reúne sua situação migratória e seu histórico previdenciário para preparar uma análise profissional mais objetiva.</p>
          <div className="hero-actions">
            {canStartAssessment
              ? <button className="primary large" onClick={startAssessment}>Começar minha triagem <ArrowRight size={19} /></button>
              : <button className="primary large" onClick={openCases}>Abrir painel de casos <ArrowRight size={19} /></button>}
            <a className="secondary light" href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer"><MessageCircle size={19} /> Falar no WhatsApp</a>
          </div>
          <div className="hero-trust"><ShieldCheck size={18} /> Resultado informativo. A análise jurídica é sempre humana.</div>
        </div>
      </section>

      <section className="intro band">
        <div className="section-heading">
          <p className="eyebrow">Um ponto de partida</p>
          <h2>Dois países podem significar duas histórias que precisam conversar.</h2>
          <p>A triagem não promete visto, benefício ou aposentadoria. Ela organiza informações para identificar o que merece análise especializada.</p>
        </div>
        <div className="feature-grid">
          <Feature icon={Plane} title="Contexto migratório" text="País, tempo de residência, status atual e objetivo de mobilidade." />
          <Feature icon={ClipboardCheck} title="Histórico contributivo" text="Períodos declarados no Brasil e no exterior reunidos numa visão inicial." />
          <Feature icon={Route} title="Próximos passos" text="Checklist objetivo para preparar documentos e uma conversa profissional." />
        </div>
      </section>

      <section className="process band tinted">
        <div className="section-heading compact">
          <p className="eyebrow">Como funciona</p>
          <h2>Clareza antes da consulta.</h2>
        </div>
        <div className="process-list">
          <Process number="01" title="Conte seu momento" text="A triagem leva cerca de quatro minutos." />
          <Process number="02" title="Receba seu mapa inicial" text="Veja pontos de atenção e documentos úteis." />
          <Process number="03" title="Decida o próximo passo" text="Leve um contexto organizado para atendimento humano." />
        </div>
      </section>

      <section className="specialist band">
        <div className="specialist-copy">
          <p className="eyebrow">Atendimento internacional</p>
          <h2>Planejamento construído para quem vive fora.</h2>
          <p>Atendimento online para brasileiros em diferentes países, com foco inicial em Portugal e Europa.</p>
          <div className="specialist-points">
            <span><Check size={18} /> Atendimento remoto</span>
            <span><Check size={18} /> Organização documental</span>
            <span><Check size={18} /> Estratégia personalizada após contratação</span>
          </div>
        </div>
        <aside className="disclaimer-block">
          <CircleAlert size={24} />
          <div><strong>Transparência desde o início</strong><p>Esta plataforma oferece triagem e conteúdo geral. Nenhum resultado exibido constitui parecer jurídico, concessão de benefício ou garantia de regularização.</p></div>
        </aside>
      </section>
    </>
  );
}

function Feature({ icon: Icon, title, text }) {
  return <article className="feature"><span><Icon size={22} /></span><h3>{title}</h3><p>{text}</p></article>;
}

function Process({ number, title, text }) {
  return <article className="process-item"><strong>{number}</strong><div><h3>{title}</h3><p>{text}</p></div></article>;
}

function Assessment({ form, setForm, step, setStep, submit, busy }) {
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const canContinue = validateStep(step, form);
  return (
    <section className="assessment-page">
      <div className="assessment-shell">
        <aside className="assessment-aside">
          <p className="eyebrow">Triagem inicial</p>
          <h1>Seu mapa começa aqui.</h1>
          <p>Responda apenas o que souber. As informações serão usadas para organizar seu contato e não substituem uma análise profissional.</p>
          <ol>
            {steps.map((item, index) => <li key={item.title} className={index === step ? "active" : index < step ? "done" : ""}><span>{index < step ? <Check size={15} /> : index + 1}</span>{item.title}</li>)}
          </ol>
        </aside>
        <div className="assessment-form">
          <div className="mobile-progress"><span style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
          <div className="form-heading"><span>Etapa {step + 1} de {steps.length}</span><h2>{steps[step].title}</h2><p>{steps[step].subtitle}</p></div>
          {step === 0 && <StepOne form={form} update={update} />}
          {step === 1 && <StepTwo form={form} update={update} />}
          {step === 2 && <StepThree form={form} update={update} />}
          {step === 3 && <StepFour form={form} update={update} />}
          <div className="form-actions">
            <button className="secondary" disabled={step === 0 || busy} onClick={() => setStep(step - 1)}><ArrowLeft size={18} /> Voltar</button>
            {step < steps.length - 1 ? (
              <button className="primary" disabled={!canContinue || busy} onClick={() => setStep(step + 1)}>Continuar <ArrowRight size={18} /></button>
            ) : (
              <button className="primary" disabled={!canContinue || busy} onClick={submit}>{busy ? "Salvando..." : "Gerar mapa inicial"} <Sparkles size={18} /></button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepOne({ form, update }) {
  return <div className="fields two-col">
    <Field label="País onde vive hoje"><select value={form.country} onChange={(e) => update("country", e.target.value)}><option>Portugal</option><option>Brasil</option><option>Espanha</option><option>França</option><option>Reino Unido</option><option>Estados Unidos</option><option>Outro</option></select></Field>
    <Field label="Nacionalidade"><input value={form.nationality} onChange={(e) => update("nationality", e.target.value)} placeholder="Ex.: Brasileira" /></Field>
    <Field label="Principal objetivo" wide><ChoiceGrid value={form.objective} onChange={(value) => update("objective", value)} options={[{ value: "regularizar", label: "Regularizar minha situação", icon: ShieldCheck }, { value: "emigrar", label: "Planejar uma mudança", icon: Plane }, { value: "aposentadoria", label: "Planejar aposentadoria", icon: CalendarDays }, { value: "planejamento", label: "Organizar tudo em conjunto", icon: Map }]} /></Field>
  </div>;
}

function StepTwo({ form, update }) {
  return <div className="fields two-col">
    <Field label="Situação migratória declarada" wide><select value={form.migrationStatus} onChange={(e) => update("migrationStatus", e.target.value)}><option value="regular">Regular / documento válido</option><option value="analysis">Pedido em análise</option><option value="expiring">Documento próximo do vencimento</option><option value="irregular">Sem regularização atual</option><option value="not_applicable">Ainda não emigrei</option><option value="unknown">Não sei informar</option></select></Field>
    <Field label="Há quantos anos vive no país atual?"><input type="number" min="0" max="80" value={form.residenceYears} onChange={(e) => update("residenceYears", e.target.value)} placeholder="Ex.: 4" /></Field>
    <Field label="Faixa de renda mensal"><select value={form.currentIncomeRange} onChange={(e) => update("currentIncomeRange", e.target.value)}><option value="">Prefiro não informar</option><option value="até 1000">Até €1.000</option><option value="1000-2500">€1.000 a €2.500</option><option value="2500-5000">€2.500 a €5.000</option><option value="5000+">Acima de €5.000</option></select></Field>
  </div>;
}

function StepThree({ form, update }) {
  return <div className="fields two-col">
    <Field label="Sua idade"><input type="number" min="16" max="100" value={form.age} onChange={(e) => update("age", e.target.value)} placeholder="Ex.: 42" /></Field>
    <Field label="Anos contribuídos no Brasil"><input type="number" min="0" max="80" step="0.5" value={form.brazilContributionYears} onChange={(e) => update("brazilContributionYears", e.target.value)} placeholder="Pode ser aproximado" /></Field>
    <Field label="Anos contribuídos no exterior"><input type="number" min="0" max="80" step="0.5" value={form.foreignContributionYears} onChange={(e) => update("foreignContributionYears", e.target.value)} placeholder="Pode ser aproximado" /></Field>
    <Field label="Objetivo de renda futura"><input value={form.futureIncomeGoal} onChange={(e) => update("futureIncomeGoal", e.target.value)} placeholder="Ex.: €1.500 por mês" /></Field>
    <div className="inline-info wide"><CircleAlert size={18} /><span>Esta triagem não calcula benefício. Ela apenas organiza os períodos declarados para uma futura conferência documental.</span></div>
  </div>;
}

function StepFour({ form, update }) {
  return <div className="fields two-col">
    <Field label="Nome completo" wide><input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Como devemos chamar você?" /></Field>
    <Field label="WhatsApp"><input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Inclua o código do país" /></Field>
    <Field label="E-mail"><input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="voce@email.com" /></Field>
    <label className="consent wide"><input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} /><span><strong>Autorizo o uso destes dados para triagem e contato.</strong><small>Seus dados não devem ser usados para decisão automatizada nem compartilhados fora do atendimento sem base legal.</small></span></label>
  </div>;
}

function ChoiceGrid({ value, onChange, options }) {
  return <div className="choice-grid">{options.map(({ value: optionValue, label, icon: Icon }) => <button type="button" key={optionValue} className={value === optionValue ? "active" : ""} onClick={() => onChange(optionValue)}><Icon size={21} /><span>{label}</span>{value === optionValue && <Check size={17} />}</button>)}</div>;
}

function Field({ label, children, wide = false }) {
  return <label className={wide ? "field wide" : "field"}><span>{label}</span>{children}</label>;
}

function Result({ result, form, restart }) {
  const message = encodeURIComponent(`Olá! Concluí a triagem da JusPrevi. Meu nome é ${form.name} e gostaria de conversar sobre meu caso.`);
  return <section className="result-page band">
    <div className="result-heading"><span className="result-icon"><ClipboardCheck size={28} /></span><p className="eyebrow">Mapa inicial concluído</p><h1>{form.name ? `${firstName(form.name)}, este é o seu ponto de partida.` : "Este é o seu ponto de partida."}</h1><p>O conteúdo abaixo organiza o que você informou. Ele não determina direito, benefício, visto ou estratégia jurídica.</p></div>
    <div className="result-layout">
      <div className="result-main">
        <section className="result-section"><div className="section-title"><Map size={22} /><div><span>Situação declarada</span><h2>Seu contexto internacional</h2></div></div><dl className="summary-list"><div><dt>Residência atual</dt><dd>{form.country}</dd></div><div><dt>Objetivo principal</dt><dd>{objectiveLabel(form.objective)}</dd></div><div><dt>Status informado</dt><dd>{migrationLabel(form.migrationStatus)}</dd></div><div><dt>Contribuições declaradas</dt><dd>{numberOrZero(form.brazilContributionYears)} anos no Brasil · {numberOrZero(form.foreignContributionYears)} no exterior</dd></div></dl></section>
        <section className="result-section"><div className="section-title"><Route size={22} /><div><span>Leitura preliminar</span><h2>Pontos para aprofundar</h2></div></div><div className="insight-list">{result.insights.map((item) => <article key={item.title}><span><ChevronRight size={17} /></span><div><h3>{item.title}</h3><p>{item.text}</p></div></article>)}</div></section>
        <section className="result-section"><div className="section-title"><ClipboardCheck size={22} /><div><span>Preparação</span><h2>Documentos que podem ajudar</h2></div></div><div className="document-grid">{result.documents.map((item) => <span key={item}><Check size={16} /> {item}</span>)}</div></section>
      </div>
      <aside className="result-aside"><p className="eyebrow">Próximo passo</p><h2>Transforme a triagem em análise.</h2><p>Um profissional precisa conferir documentos, datas, regras aplicáveis e seu objetivo antes de recomendar qualquer estratégia.</p><a className="primary full" href={`https://wa.me/${WHATSAPP}?text=${message}`} target="_blank" rel="noreferrer"><MessageCircle size={19} /> Conversar no WhatsApp</a><button className="secondary full" onClick={restart}>Refazer triagem</button><small><LockKeyhole size={14} /> Não envie passaporte ou documentos sensíveis pelo formulário público.</small></aside>
    </div>
  </section>;
}

function CasesPanel({ user, isProfessional, isAdmin, leads, openAuth, setNotice }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  if (!user) return <AccessState icon={LogIn} title="Entre para acessar o CRM" text="O painel administrativo exige autenticação." action={openAuth} actionLabel="Entrar" />;
  if (!isProfessional) return <AccessState icon={LockKeyhole} title="Acesso restrito" text="Sua conta não possui permissão profissional para consultar casos." />;
  const countryCount = leads.reduce((acc, item) => ({ ...acc, [item.country || "Não informado"]: (acc[item.country || "Não informado"] || 0) + 1 }), {});
  const topCountry = Object.entries(countryCount).sort((a, b) => b[1] - a[1])[0];
  return <section className="admin-page band">
    <div className="admin-heading"><div><p className="eyebrow">Área profissional</p><h1>Casos e triagens</h1><p>Consulte as informações recebidas e abra a ficha completa de cada pessoa.</p></div><span className="live-badge"><span /> Atualização em tempo real</span></div>
    <div className="admin-kpis"><Kpi label="Novas triagens" value={leads.length} detail="Registros mais recentes" /><Kpi label="Principal origem" value={topCountry?.[0] || "—"} detail={topCountry ? `${topCountry[1]} pessoa(s)` : "Sem dados"} /><Kpi label="Atenção prioritária" value={leads.filter((item) => item.priority === "high").length} detail="Pelo contexto declarado" /></div>
    <section className="lead-panel"><div className="panel-title"><div><h2>Fila de contatos</h2><p>Ordenada do mais recente para o mais antigo.</p></div><span>{leads.length} registros</span></div>{leads.length ? <div className="lead-list">{leads.map((lead) => <article className="lead-row" key={lead.id}><div className="lead-person"><span>{initials(lead.name)}</span><div><strong>{lead.name}</strong><small>{lead.country} · {objectiveLabel(lead.objective)}</small></div></div><div className="lead-contact"><span>{lead.phone}</span><small>{lead.email || "Sem e-mail"}</small></div><span className={`priority ${lead.priority}`}>{lead.priority === "high" ? "Prioritário" : "Regular"}</span><a className="icon-button" href={`https://wa.me/${phoneDigits(lead.phone)}`} target="_blank" rel="noreferrer" title="Abrir WhatsApp"><MessageCircle size={19} /></a><button className="case-button" onClick={() => setSelectedLead(lead)}><FolderOpen size={18} /> Abrir caso</button></article>)}</div> : <div className="empty-state"><BriefcaseBusiness size={28} /><strong>Nenhuma triagem recebida</strong><span>Assim que alguém concluir o formulário, aparecerá aqui.</span></div>}</section>
    {selectedLead && !deletingLead && <LeadCaseModal lead={selectedLead} close={() => setSelectedLead(null)} canDelete={isAdmin} requestDelete={() => setDeletingLead(selectedLead)} />}
    {deletingLead && <DeleteCaseDialog lead={deletingLead} close={() => setDeletingLead(null)} deleted={() => { setDeletingLead(null); setSelectedLead(null); setNotice({ type: "success", text: `Caso de ${deletingLead.name} excluído com sucesso.` }); }} />}
  </section>;
}

function AdminPanel({ user, isAdmin, users, accessLogs, openAuth, setNotice, startSimulation }) {
  if (!user) return <AccessState icon={LogIn} title="Entre para administrar" text="A gestão de acessos exige autenticação." action={openAuth} actionLabel="Entrar" />;
  if (!isAdmin) return <AccessState icon={LockKeyhole} title="Acesso de administrador necessário" text="Advogados podem consultar casos, mas não podem alterar usuários ou permissões." />;
  const activeUsers = users.filter((entry) => entry.status !== "blocked").length;
  const lawyers = users.filter((entry) => normalizeRole(entry.role) === "lawyer" && entry.status !== "blocked").length;
  const blocked = users.filter((entry) => entry.status === "blocked").length;
  return <section className="admin-page band">
    <div className="admin-heading"><div><p className="eyebrow">Administração</p><h1>Usuários e permissões</h1><p>Defina quem é cliente, advogado ou administrador e suspenda contas quando necessário.</p></div><span className="admin-badge"><ShieldCheck size={16} /> Acesso protegido</span></div>
    <AccessSimulator users={users} startSimulation={startSimulation} />
    <div className="admin-kpis"><Kpi label="Contas ativas" value={activeUsers} detail={`${users.length} cadastrada(s)`} /><Kpi label="Advogados" value={lawyers} detail="Com acesso aos casos" /><Kpi label="Contas bloqueadas" value={blocked} detail="Sem acesso a dados privados" /></div>
    <AccessManager users={users} currentUser={user} setNotice={setNotice} />
    <AccessAudit logs={accessLogs} />
  </section>;
}

function AccessSimulator({ users, startSimulation }) {
  const clients = users.filter((entry) => normalizeRole(entry.role) === "client" && entry.status !== "blocked");
  const [selectedClientId, setSelectedClientId] = useState("demo");
  const selectedClient = clients.find((entry) => entry.id === selectedClientId);
  return <section className="simulation-panel">
    <div className="panel-title"><div><h2>Simular um acesso</h2><p>Confira exatamente quais páginas e opções cada perfil encontra no site.</p></div><span>Prévia segura</span></div>
    <div className="simulation-options">
      <article>
        <span className="simulation-icon"><BriefcaseBusiness size={22} /></span>
        <div><strong>Advogado</strong><small>Visualiza o CRM, os casos e as triagens completas, sem administrar usuários.</small></div>
        <button className="secondary" onClick={() => startSimulation({ role: "lawyer", name: "Advogado de teste" })}><Eye size={17} /> Simular advogado</button>
      </article>
      <article>
        <span className="simulation-icon"><UserRound size={22} /></span>
        <div><strong>Cliente</strong><small>Visualiza somente a própria área e as triagens vinculadas à conta escolhida.</small></div>
        <label><span>Conta para visualizar</span><select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)}><option value="demo">Cliente de teste (sem dados)</option>{clients.map((entry) => <option key={entry.id} value={entry.id}>{entry.name || entry.email || "Cliente"}</option>)}</select></label>
        <button className="secondary" onClick={() => startSimulation({ role: "client", userId: selectedClient?.id || null, name: selectedClient?.name || "Cliente de teste" })}><Eye size={17} /> Simular cliente</button>
      </article>
    </div>
  </section>;
}

function LeadCaseModal({ lead, close, canDelete, requestDelete }) {
  useEffect(() => {
    const handleKey = (event) => event.key === "Escape" && close();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [close]);
  const suggestedDocuments = buildResult(lead).documents;
  return <div className="modal-backdrop case-backdrop" onMouseDown={(event) => event.target === event.currentTarget && close()}>
    <article className="case-modal" role="dialog" aria-modal="true" aria-label={`Caso de ${lead.name}`}>
      <header className="case-header"><div className="lead-person"><span>{initials(lead.name)}</span><div><p className="eyebrow">Ficha do caso</p><h2>{lead.name}</h2><small>Criado em {formatDateTime(lead.createdAt)} · ID {lead.id}</small></div></div><button className="modal-close" onClick={close} aria-label="Fechar caso"><X /></button></header>
      <div className="case-status"><span className={`priority ${lead.priority}`}>{lead.priority === "high" ? "Atenção prioritária" : "Triagem regular"}</span><span>{caseStatusLabel(lead.status)}</span><span>{objectiveLabel(lead.objective)}</span></div>
      <section className="case-section"><div className="case-section-title"><UserRound size={20} /><div><span>Identificação</span><h3>Contato e perfil</h3></div></div><dl className="case-data"><DataItem label="Nome" value={lead.name} /><DataItem label="WhatsApp" value={lead.phone} /><DataItem label="E-mail" value={lead.email || "Não informado"} /><DataItem label="Nacionalidade" value={lead.nationality || "Não informada"} /></dl></section>
      <section className="case-section"><div className="case-section-title"><Plane size={20} /><div><span>Imigração</span><h3>Contexto internacional</h3></div></div><dl className="case-data"><DataItem label="País atual" value={lead.country || "Não informado"} /><DataItem label="Situação declarada" value={migrationLabel(lead.migrationStatus)} /><DataItem label="Tempo de residência" value={lead.residenceYears === "" || lead.residenceYears == null ? "Não informado" : `${lead.residenceYears} ano(s)`} /><DataItem label="Objetivo" value={objectiveLabel(lead.objective)} /></dl></section>
      <section className="case-section"><div className="case-section-title"><CalendarDays size={20} /><div><span>Previdência</span><h3>Histórico declarado</h3></div></div><dl className="case-data"><DataItem label="Idade" value={lead.age ? `${lead.age} anos` : "Não informada"} /><DataItem label="Contribuição no Brasil" value={`${numberOrZero(lead.brazilContributionYears)} ano(s)`} /><DataItem label="Contribuição no exterior" value={`${numberOrZero(lead.foreignContributionYears)} ano(s)`} /><DataItem label="Meta de renda futura" value={lead.futureIncomeGoal || "Não informada"} /><DataItem label="Faixa de renda atual" value={incomeLabel(lead.currentIncomeRange)} /><DataItem label="Consentimento" value={lead.consent ? "Registrado" : "Não registrado"} /></dl></section>
      <section className="case-section documents-section"><div className="case-section-title"><FileText size={20} /><div><span>Documentos</span><h3>Documentos recomendados</h3></div></div><p className="section-help">O envio de arquivos ainda não está habilitado. Esta lista orienta a conferência documental do atendimento.</p><div className="case-documents">{suggestedDocuments.map((item) => <div key={item}><span><FileText size={17} /> {item}</span><strong>Aguardando envio</strong></div>)}</div></section>
      <footer className="case-footer">{canDelete && <button className="danger-button" onClick={requestDelete}>Excluir caso</button>}<a className="secondary" href={`https://wa.me/${phoneDigits(lead.phone)}`} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Abrir WhatsApp</a><button className="primary" onClick={close}>Fechar caso</button></footer>
    </article>
  </div>;
}

function DeleteCaseDialog({ lead, close, deleted }) {
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const confirmed = confirmation.trim().toLowerCase() === "excluir";
  useEffect(() => {
    const handleKey = (event) => event.key === "Escape" && !busy && close();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [busy, close]);
  const removeCase = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    setError("");
    try {
      await deleteDoc(doc(db, "leads", lead.id));
      deleted();
    } catch (deleteError) {
      setError(readableError(deleteError));
      setBusy(false);
    }
  };
  return <div className="modal-backdrop delete-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !busy && close()}>
    <article className="delete-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-case-title">
      <span className="delete-icon"><CircleAlert size={26} /></span>
      <p className="eyebrow">Ação permanente</p>
      <h2 id="delete-case-title">Excluir o caso de {lead.name}?</h2>
      <p>O caso será removido da fila profissional e não poderá ser recuperado por este painel.</p>
      <label><span>Para confirmar, digite <strong>EXCLUIR</strong></span><input autoFocus value={confirmation} disabled={busy} onChange={(event) => setConfirmation(event.target.value)} placeholder="EXCLUIR" autoComplete="off" /></label>
      {error && <div className="delete-error"><CircleAlert size={17} /> {error}</div>}
      <div className="delete-actions"><button className="secondary" disabled={busy} onClick={close}>Cancelar</button><button className="danger-button" disabled={!confirmed || busy} onClick={removeCase}>{busy ? "Excluindo..." : "Excluir definitivamente"}</button></div>
    </article>
  </div>;
}

function DataItem({ label, value }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function AccessManager({ users, currentUser, setNotice }) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const filteredUsers = users.filter((entry) => {
    const matchesText = `${entry.name || ""} ${entry.email || ""}`.toLowerCase().includes(search.trim().toLowerCase());
    const matchesRole = roleFilter === "all" || normalizeRole(entry.role) === roleFilter || (roleFilter === "blocked" && entry.status === "blocked");
    return matchesText && matchesRole;
  });
  return <section className="access-panel">
    <div className="panel-title"><div><h2>Controle de acessos</h2><p>Clientes veem apenas as próprias triagens. Advogados consultam casos. Administradores também gerenciam permissões.</p></div><span>{filteredUsers.length} de {users.length}</span></div>
    <div className="access-toolbar">
      <label className="search-field"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Pesquisar nome ou e-mail" /></label>
      <label><span>Filtrar</span><select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}><option value="all">Todos os acessos</option><option value="client">Clientes</option><option value="lawyer">Advogados</option><option value="admin">Administradores</option><option value="blocked">Bloqueados</option></select></label>
    </div>
    {filteredUsers.length ? <div className="user-list">{filteredUsers.map((entry) => <AccessUserRow key={entry.id} entry={entry} currentUser={currentUser} setNotice={setNotice} />)}</div> : <div className="empty-state"><UsersRound size={28} /><strong>Nenhuma conta encontrada</strong><span>Ajuste a pesquisa ou aguarde o primeiro cadastro.</span></div>}
  </section>;
}

function AccessUserRow({ entry, currentUser, setNotice }) {
  const storedRole = normalizeRole(entry.role);
  const storedStatus = entry.status === "blocked" ? "blocked" : "active";
  const [role, setRole] = useState(storedRole);
  const [status, setStatus] = useState(storedStatus);
  const [saving, setSaving] = useState(false);
  const protectedAdmin = entry.email?.toLowerCase() === ADMIN_EMAIL;
  const changed = role !== storedRole || status !== storedStatus || entry.role === "supervisor";

  useEffect(() => {
    setRole(storedRole);
    setStatus(storedStatus);
  }, [storedRole, storedStatus]);

  const saveAccess = async () => {
    if (!changed || protectedAdmin) return;
    const description = `${roleLabel(storedRole)} → ${roleLabel(role)}; ${statusLabel(storedStatus)} → ${statusLabel(status)}`;
    if (!window.confirm(`Confirmar a alteração de ${entry.name || entry.email}?\n\n${description}`)) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      batch.set(doc(db, "users", entry.id), {
        role,
        status,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      }, { merge: true });
      batch.set(doc(collection(db, "accessLogs")), {
        actorUid: currentUser.uid,
        actorEmail: currentUser.email?.toLowerCase() || "",
        targetUid: entry.id,
        targetName: entry.name || "",
        targetEmail: entry.email || "",
        fromRole: storedRole,
        toRole: role,
        fromStatus: storedStatus,
        toStatus: status,
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      setNotice({ type: "success", text: `Acesso de ${entry.name || entry.email} atualizado com sucesso.` });
    } catch (error) {
      setNotice({ type: "error", text: readableError(error) });
    } finally {
      setSaving(false);
    }
  };

  return <article className={status === "blocked" ? "blocked" : ""}>
    <div className="user-identity"><div className="lead-person"><span>{initials(entry.name || entry.email)}</span><div><strong>{entry.name || "Sem nome"}</strong><small>{entry.email || "Sem e-mail"}</small></div></div><small>Criada em {formatDate(entry.createdAt)} · Último acesso {formatDateTime(entry.lastLoginAt)}</small></div>
    <label><span>Perfil</span><select value={role} disabled={protectedAdmin || saving} onChange={(event) => setRole(event.target.value)}><option value="client">Cliente</option><option value="lawyer">Advogado</option><option value="admin">Administrador</option></select></label>
    <label><span>Situação</span><select value={status} disabled={protectedAdmin || saving} onChange={(event) => setStatus(event.target.value)}><option value="active">Ativo</option><option value="blocked">Bloqueado</option></select></label>
    <button className="primary access-save" disabled={!changed || protectedAdmin || saving} onClick={saveAccess}>{saving ? "Salvando..." : <><Save size={17} /> Salvar</>}</button>
    {protectedAdmin && <span className="protected-account"><ShieldCheck size={15} /> Administrador principal</span>}
  </article>;
}

function AccessAudit({ logs }) {
  return <section className="access-panel audit-panel">
    <div className="panel-title"><div><h2>Histórico de permissões</h2><p>Registro das alterações realizadas pelos administradores.</p></div><span>{logs.length} recente(s)</span></div>
    {logs.length ? <div className="audit-list">{logs.map((entry) => <article key={entry.id}><span className="audit-icon"><History size={18} /></span><div><strong>{entry.targetName || entry.targetEmail || "Usuário"}</strong><small>{roleLabel(entry.fromRole)} → {roleLabel(entry.toRole)} · {statusLabel(entry.fromStatus)} → {statusLabel(entry.toStatus)}</small></div><div className="audit-meta"><span>{formatDateTime(entry.createdAt)}</span><small>por {entry.actorEmail || "administrador"}</small></div></article>)}</div> : <div className="empty-state"><History size={28} /><strong>Nenhuma alteração registrada</strong><span>As próximas mudanças de acesso aparecerão aqui.</span></div>}
  </section>;
}

function ClientArea({ user, profile, accountActive, assessments, openAuth, startAssessment }) {
  if (!user) return <AccessState icon={LogIn} title="Entre para ver sua área" text="Suas triagens salvas ficam disponíveis depois do login." action={openAuth} actionLabel="Entrar" />;
  if (!accountActive) return <AccessState icon={Ban} title="Conta temporariamente bloqueada" text="Seu acesso foi suspenso. Entre em contato com a equipe JusPrevi para verificar sua conta." />;
  return <section className="client-page band">
    <div className="client-heading">
      <div><p className="eyebrow">Área do cliente</p><h1>Olá, {firstName(profile?.name || user.displayName || "visitante")}.</h1><p>Consulte os contextos que você já enviou e prepare o próximo atendimento.</p></div>
      <button className="primary" onClick={startAssessment}>Nova triagem <ArrowRight size={18} /></button>
    </div>
    <section className="assessment-history">
      <div className="panel-title"><div><h2>Minhas triagens</h2><p>Somente você e a equipe autorizada podem consultar estes registros.</p></div><span>{assessments.length} registro(s)</span></div>
      {assessments.length ? <div className="history-list">{assessments.map((item) => <article key={item.id}><span className="history-icon"><Map size={20} /></span><div><strong>{objectiveLabel(item.objective)}</strong><small>{item.country || "País não informado"} · {formatDate(item.createdAt)}</small></div><span className={`priority ${item.priority}`}>{item.priority === "high" ? "Requer atenção" : "Informativo"}</span></article>)}</div> : <div className="empty-state"><ClipboardCheck size={28} /><strong>Nenhuma triagem salva nesta conta</strong><span>Faça uma nova triagem enquanto estiver conectado para guardá-la aqui.</span></div>}
    </section>
  </section>;
}

function Kpi({ label, value, detail }) {
  return <article><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function AccessState({ icon: Icon, title, text, action, actionLabel }) {
  return <section className="access-state"><Icon size={34} /><h1>{title}</h1><p>{text}</p>{action && <button className="primary" onClick={action}>{actionLabel}</button>}</section>;
}

function AuthModal({ mode, setMode, close, setNotice, setBusy, busy }) {
  const [values, setValues] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const credential = await createUserWithEmailAndPassword(auth, values.email, values.password);
        await updateProfile(credential.user, { displayName: values.name });
        await setDoc(doc(db, "users", credential.user.uid), { name: values.name, email: values.email.toLowerCase(), role: "client", status: "active", provider: "password", createdAt: serverTimestamp(), lastLoginAt: serverTimestamp() });
      } else {
        await signInWithEmailAndPassword(auth, values.email, values.password);
      }
      setNotice({ type: "success", text: mode === "register" ? "Conta criada com sucesso." : "Login realizado." });
      close();
    } catch (error) {
      setNotice({ type: "error", text: readableError(error) });
    } finally {
      setBusy(false);
    }
  };
  const google = async () => {
    setBusy(true);
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const ref = doc(db, "users", credential.user.uid);
      const existing = await getDoc(ref);
      if (!existing.exists() && credential.user.email?.toLowerCase() !== ADMIN_EMAIL) {
        await setDoc(ref, { name: credential.user.displayName || "Cliente", email: credential.user.email?.toLowerCase() || "", role: "client", status: "active", provider: "google", createdAt: serverTimestamp(), lastLoginAt: serverTimestamp() });
      }
      setNotice({ type: "success", text: "Login realizado." });
      close();
    } catch (error) {
      setNotice({ type: "error", text: readableError(error) });
    } finally {
      setBusy(false);
    }
  };
  const resetPassword = async () => {
    if (!values.email.trim()) {
      setNotice({ type: "error", text: "Digite seu e-mail para receber o link de recuperação." });
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, values.email.trim());
      setNotice({ type: "success", text: "Enviamos um link de recuperação para o seu e-mail." });
    } catch (error) {
      setNotice({ type: "error", text: readableError(error) });
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && close()}><div className="auth-modal" role="dialog" aria-modal="true"><button className="modal-close" onClick={close}><X /></button><span className="brand-mark"><UserRound size={25} /></span><h2>{mode === "register" ? "Criar minha conta" : "Entrar na JusPrevi"}</h2><p>{mode === "register" ? "Salve suas futuras análises em uma área pessoal." : "Acesse sua conta ou o painel profissional."}</p><form onSubmit={submit}>{mode === "register" && <Field label="Nome"><input required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} /></Field>}<Field label="E-mail"><input type="email" required value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} /></Field><Field label="Senha"><span className="password-field"><input type={showPassword ? "text" : "password"} minLength="6" required value={values.password} onChange={(e) => setValues({ ...values, password: e.target.value })} /><button type="button" onClick={() => setShowPassword(!showPassword)} title={showPassword ? "Ocultar senha" : "Mostrar senha"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></Field>{mode === "login" && <button type="button" className="forgot-password" disabled={busy} onClick={resetPassword}>Esqueci minha senha</button>}<button className="primary full" disabled={busy}>{busy ? "Aguarde..." : mode === "register" ? "Criar conta" : "Entrar"}</button></form><div className="auth-divider"><span>ou</span></div><button className="secondary full" disabled={busy} onClick={google}>Continuar com Google</button><button className="auth-switch" onClick={() => setMode(mode === "register" ? "login" : "register")}>{mode === "register" ? "Já tenho uma conta" : "Ainda não tenho conta"}</button></div></div>;
}

function Toast({ notice, close }) {
  return <div className={`toast ${notice.type}`} role="status"><span>{notice.type === "success" ? <Check /> : <CircleAlert />}</span><p>{notice.text}</p><button onClick={close}><X size={18} /></button></div>;
}

function BootScreen() {
  return <div className="boot-screen" role="status"><span className="brand-mark"><Globe2 size={25} /></span><strong>JusPrevi</strong><div className="boot-spinner" /><small>Preparando seu acesso...</small></div>;
}

function Footer({ startAssessment, canStartAssessment, openCases }) {
  return <footer><div className="footer-brand"><span className="brand-mark"><Globe2 size={22} /></span><div><strong>JusPrevi</strong><span>Imigração e Previdência Internacional</span></div></div><div className="footer-links">{canStartAssessment ? <button onClick={startAssessment}>Fazer triagem</button> : <button onClick={openCases}>Casos</button>}<a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer">WhatsApp</a></div><p>© 2026 JusPrevi · Triagem informativa. Não constitui parecer jurídico ou previdenciário.</p></footer>;
}

function buildResult(data) {
  const insights = [];
  if (["irregular", "expiring"].includes(data.migrationStatus)) insights.push({ title: "Situação migratória merece atenção", text: "Prazos, documentos e o histórico completo precisam ser conferidos por profissional habilitado antes de qualquer decisão." });
  else if (data.migrationStatus === "analysis") insights.push({ title: "Acompanhar o processo em análise", text: "Organize protocolo, comprovativos e comunicações recebidas para revisar o andamento e possíveis exigências." });
  else insights.push({ title: "Revisar a manutenção da regularidade", text: "Mesmo com documento válido, renovações, alterações familiares e objetivos futuros podem exigir planejamento prévio." });
  const brazil = Number(data.brazilContributionYears || 0);
  const abroad = Number(data.foreignContributionYears || 0);
  if (brazil > 0 && abroad > 0) insights.push({ title: "Há períodos em mais de um país", text: "É importante confirmar vínculos, datas e eventual acordo internacional aplicável. A existência de períodos não garante soma automática ou benefício." });
  else if (brazil > 0) insights.push({ title: "Histórico brasileiro informado", text: "Um extrato contributivo atualizado ajuda a localizar lacunas, vínculos e divergências antes de qualquer projeção." });
  else insights.push({ title: "Histórico contributivo ainda incompleto", text: "A ausência de períodos informados não permite conclusão. Reúna extratos oficiais dos países em que trabalhou ou contribuiu." });
  insights.push({ title: "Objetivo financeiro precisa de números verificáveis", text: data.futureIncomeGoal ? `A meta declarada foi ${data.futureIncomeGoal}. Ela deve ser confrontada com contribuições, idade, regras vigentes e capacidade financeira.` : "Definir uma renda futura desejada ajuda a comparar cenários, mas exige dados oficiais e premissas documentadas." });
  return {
    priority: ["irregular", "expiring"].includes(data.migrationStatus) ? "high" : "normal",
    insights,
    documents: ["Documento de identificação válido", "Comprovativos do status migratório", "Extrato contributivo do Brasil (CNIS)", "Extratos ou registros contributivos no exterior", "Linha do tempo de residência e trabalho"],
  };
}

function validateStep(index, form) {
  if (index === 0) return Boolean(form.country && form.nationality && form.objective);
  if (index === 1) return Boolean(form.migrationStatus);
  if (index === 2) return Boolean(form.age);
  return Boolean(form.name.trim() && form.phone.trim() && form.consent);
}

function objectiveLabel(value) {
  return ({ regularizar: "Regularização migratória", emigrar: "Planejamento de mudança", aposentadoria: "Planejamento previdenciário", planejamento: "Planejamento integrado" })[value] || "Planejamento internacional";
}

function migrationLabel(value) {
  return ({ regular: "Documento válido", analysis: "Pedido em análise", expiring: "Documento próximo do vencimento", irregular: "Sem regularização atual", not_applicable: "Ainda não emigrou", unknown: "Não soube informar" })[value] || "Não informado";
}

function incomeLabel(value) {
  return ({ "até 1000": "Até €1.000", "1000-2500": "€1.000 a €2.500", "2500-5000": "€2.500 a €5.000", "5000+": "Acima de €5.000" })[value] || "Não informada";
}

function caseStatusLabel(value) {
  return ({ new: "Novo contato", contacted: "Contato realizado", analysis: "Em análise", closed: "Concluído" })[value] || "Novo contato";
}

function normalizeRole(value) {
  if (value === "supervisor") return "lawyer";
  return ["client", "lawyer", "admin"].includes(value) ? value : "client";
}

function roleLabel(value) {
  return ({ client: "Cliente", lawyer: "Advogado", admin: "Administrador", supervisor: "Advogado" })[value] || "Cliente";
}

function statusLabel(value) {
  return value === "blocked" ? "Bloqueado" : "Ativo";
}

function readableError(error) {
  const code = String(error?.code || "");
  if (code.includes("operation-not-allowed")) return "Este método de login ainda precisa ser ativado no Firebase.";
  if (code.includes("invalid-credential")) return "E-mail ou senha incorretos.";
  if (code.includes("email-already-in-use")) return "Este e-mail já possui uma conta.";
  if (code.includes("weak-password")) return "Use uma senha com pelo menos seis caracteres.";
  if (code.includes("too-many-requests")) return "Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.";
  if (code.includes("network-request-failed")) return "Não foi possível conectar. Verifique sua internet e tente novamente.";
  if (code.includes("permission-denied")) return "O Firebase bloqueou esta operação. Verifique as permissões do projeto.";
  return error?.message || "Não foi possível concluir. Tente novamente.";
}

function firstName(name) { return String(name || "").trim().split(/\s+/)[0]; }
function numberOrZero(value) { return Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 }); }
function initials(name) { return String(name || "?").split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase(); }
function phoneDigits(phone) { return String(phone || "").replace(/\D/g, ""); }
function formatDate(timestamp) {
  if (!timestamp?.toDate) return "Agora";
  return timestamp.toDate().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}
function formatDateTime(timestamp) {
  if (!timestamp?.toDate) return "agora";
  return timestamp.toDate().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export default App;
